import React, { useEffect, useState, useCallback } from "react";
import { Search, Loader2, Flower2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { NoteCard } from "@/components/NoteCard";
import { NoteComposer } from "@/components/NoteComposer";
import { AuthDialog } from "@/components/AuthDialog";
import { useAuth } from "@/context/AuthContext";
import { Petals } from "@/components/Petals";
import { FeaturedBloom } from "@/components/FeaturedBloom";

const LETTERS = ["All", "A-D", "E-H", "I-M", "N-R", "S-Z"];

export default function Garden() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [letter, setLetter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, m] = await Promise.all([
        api.get("/notes", { params: { search: search || undefined, letter } }),
        search ? api.get("/members", { params: { search } }) : Promise.resolve({ data: [] }),
      ]);
      setNotes(n.data);
      setMembers(m.data);
    } finally {
      setLoading(false);
    }
  }, [search, letter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleLeave = () => (user ? setComposeOpen(true) : setAuthOpen(true));

  return (
    <div className="garden-bg">
      <Petals count={10} />
      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-16 pb-10 text-center">
        <p className="text-xs font-mono tracking-[0.3em] uppercase text-pink-400 mb-4">A wall of whispered affection</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-bold text-pink-50 text-glow tracking-tight">
          The Garden of<br />Love Notes
        </h1>
        <p className="max-w-2xl mx-auto mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
          Leave a note for someone who matters — signed or anonymous. Search for a name, read what
          the Garden holds, and let roses bloom behind every word.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <button
            data-testid="hero-leave-note-btn"
            onClick={handleLeave}
            className="bg-gradient-to-r from-pink-600 to-rose-500 hover:from-pink-500 hover:to-rose-400 text-white font-medium px-7 py-3.5 rounded-full shadow-lg shadow-pink-500/25 transition-all hover:scale-105 flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" /> Leave a Note
          </button>
        </div>
      </section>

      <FeaturedBloom />

      {/* Search + filters */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="relative max-w-2xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-400" />
          <input
            data-testid="garden-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search a member or a note…"
            className="w-full bg-black/40 border border-pink-500/25 rounded-full pl-12 pr-4 py-4 text-pink-50 placeholder:text-slate-500 focus:outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-500/30 transition-all"
          />
        </div>

        <div className="flex items-center justify-center gap-2 mt-6 flex-wrap">
          {LETTERS.map((l) => (
            <button
              key={l}
              data-testid={`garden-letter-${l}`}
              onClick={() => setLetter(l)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                letter === l ? "bg-pink-500 text-white" : "bg-white/5 text-slate-300 hover:bg-white/10 border border-pink-500/15"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {/* Member results */}
        {members.length > 0 && (
          <div className="mt-8" data-testid="member-results">
            <p className="text-xs font-mono tracking-[0.2em] uppercase text-pink-400 mb-3">Members</p>
            <div className="flex gap-3 flex-wrap">
              {members.map((m) => (
                <a key={m.id} data-testid={`member-chip-${m.id}`} href={`/member/${m.id}`} className="flex items-center gap-3 card-glass rounded-full pl-2 pr-5 py-2 hover:border-pink-400 border border-pink-500/20 transition-all">
                  <span className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center text-pink-300 font-serif">
                    {m.name?.[0]?.toUpperCase() || "?"}
                  </span>
                  <span>
                    <span className="block text-pink-100 text-sm">{m.name}</span>
                    <span className="block text-slate-500 text-xs">{m.notes_received} notes received</span>
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Notes grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
        {loading ? (
          <div className="flex justify-center py-20" data-testid="garden-loading">
            <Loader2 className="w-8 h-8 text-pink-400 animate-spin" />
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-20" data-testid="garden-empty">
            <Flower2 className="w-12 h-12 text-pink-500/40 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">The Garden is quiet here. Be the first to plant a note.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="notes-grid">
            {notes.map((n, i) => (
              <NoteCard key={n.id} note={n} index={i} />
            ))}
          </div>
        )}
      </section>

      <NoteComposer open={composeOpen} onOpenChange={setComposeOpen} onCreated={load} />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => setComposeOpen(true)} />
    </div>
  );
}
